import React, { useEffect, useRef } from 'react';
import images from '../../assets/images';
import { getBrand } from '../../config/branding';
import './FloatingIcons.css';

/**
 * Decorative background for the auth pages: the six Nextek application logos in
 * frosted glass tiles, drifting on three depth layers.
 *
 * Only real Nextek products appear here — no generic or third-party imagery.
 *
 * Layer 1 is nearest (largest, sharpest, widest drift), layer 3 furthest
 * (blurred and faint). Positions live in SLOTS, not on the products, so that
 * `feature` can move a product to the front without dragging its geometry along
 * — every collision-tested position, size and responsive nudge belongs to the
 * slot and applies to whichever product is standing in it.
 *
 * With no `feature`, LinenTech occupies the hero slot: it is the flagship and the
 * most prominent tile, which is the default this component was designed around.
 * A sign-in reached from `?application=<product>` promotes that product into the
 * hero slot instead and swaps LinenTech into the slot it vacated.
 *
 * Purely decorative — aria-hidden and pointer-events: none throughout.
 */

type Layer = 1 | 2 | 3;

interface Slot {
  /** Stable id for the responsive rules in the stylesheet (`data-slot`). */
  id: string;
  layer: Layer;
  top: string;
  left: string;
  /** Tile width when a wordmark stands here. */
  pill: number;
  /** Tile side length when a compact mark stands here. */
  square: number;
}

/**
 * Positions down the left and right thirds, so the centred auth card never has a
 * tile underneath it. Both sizes are given for every slot because `feature` can
 * put either tile shape in any slot; they are matched for visual mass (a pill's
 * drawn height is 0.42x its width).
 */
const SLOTS: readonly Slot[] = [
  // ---- Near layer: largest, sharpest, widest drift ----
  { id: 'hero', layer: 1, top: '44%', left: '13%', pill: 200, square: 108 },
  { id: 'near-right', layer: 1, top: '72%', left: '85%', pill: 172, square: 86 },

  // ---- Mid layer ----
  { id: 'mid-left', layer: 2, top: '15%', left: '11%', pill: 170, square: 92 },
  { id: 'mid-right', layer: 2, top: '42%', left: '84%', pill: 168, square: 90 },

  // ---- Far layer: smaller, blurred, faint, slowest ----
  { id: 'far-left', layer: 3, top: '76%', left: '9%', pill: 122, square: 66 },
  { id: 'far-right', layer: 3, top: '14%', left: '86%', pill: 148, square: 80 },
];

interface Product {
  /** Matches the `?application=` key, so `feature` can be passed straight in. */
  id: string;
  /** `square` for compact icon marks, `pill` for the wide wordmark logos. */
  shape: 'square' | 'pill';
  /** Resting tilt, so the set never looks mechanical. */
  rotate: number;
  logo: string;
}

/* Three of the six logos are wide wordmarks and three are compact marks, hence
   the two tile shapes — a 739x178 wordmark in a square tile would be unreadable.
   Order is the default slot assignment, one product per slot in SLOTS order. */
const PRODUCTS: readonly Product[] = [
  { id: 'linentech', shape: 'pill', rotate: -5, logo: images.linenTechLogo },
  { id: 'equipmate', shape: 'square', rotate: 9, logo: images.equipmateLogo },
  { id: 'linenconnect', shape: 'pill', rotate: 6, logo: images.linenConnectLogo },
  { id: 'linendesk', shape: 'pill', rotate: -5, logo: images.linenDeskLogo },
  { id: 'lineniq', shape: 'square', rotate: -7, logo: images.linenIqLogo },
  { id: 'linencrew', shape: 'pill', rotate: 8, logo: images.linenCrewLogo },
];

/**
 * A featured tile is bigger than the hero slot's normal occupant, so it needs its
 * own sizes — and its own entry in the stylesheet's responsive ladder, since
 * these arrive as inline styles that outrank every rule there. Only ~10% larger:
 * the hero slot's clearance from the card is already measured, and the emphasis
 * comes mostly from opacity and the brand-tinted glow.
 */
const FEATURED_SIZE = { pill: 220, square: 118 };

/** How far each depth layer follows the pointer, in px at full deflection. */
const PARALLAX_DEPTH: Record<Layer, number> = { 1: 22, 2: 12, 3: 6 };

const LAYERS: Layer[] = [3, 2, 1];

/** `'#2563EB'` -> `'37, 99, 235'`, for the featured tile's tinted glow. */
const toRgbChannels = (hex: string): string => {
  const value = parseInt(hex.replace('#', ''), 16);
  // eslint-disable-next-line no-bitwise
  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
};

interface Occupant extends Product {
  slot: Slot;
  featured: boolean;
}

/**
 * Which product stands in which slot. `feature` swaps that product with whatever
 * holds the hero slot — a swap rather than a reorder, so no other tile moves and
 * every measured position stays occupied by exactly one tile.
 */
const assignSlots = (feature?: string | null): Occupant[] => {
  const order = [...PRODUCTS];
  const featuredIndex = feature
    ? order.findIndex((product) => product.id === feature.toLowerCase())
    : -1;
  if (featuredIndex > 0) {
    [order[0], order[featuredIndex]] = [order[featuredIndex], order[0]];
  }
  return order.map((product, index) => ({
    ...product,
    slot: SLOTS[index],
    // Only the hero slot is ever featured, and only when a product asked for it.
    featured: index === 0 && featuredIndex !== -1,
  }));
};

interface FloatingIconsProps {
  /**
   * `?application=` value of the product to bring to the front, if any. An
   * unknown value simply leaves the default arrangement alone.
   */
  feature?: string | null;
}

const FloatingIcons: React.FC<FloatingIconsProps> = ({ feature = null }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Pointer parallax. One listener, coalesced into a single rAF per frame, and
  // written out as CSS custom properties so only the three layer wrappers move.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (reduceMotion.matches || !finePointer.matches) return;

    let frame = 0;
    let nextX = 0;
    let nextY = 0;

    const apply = () => {
      frame = 0;
      root.style.setProperty('--pointer-x', nextX.toFixed(3));
      root.style.setProperty('--pointer-y', nextY.toFixed(3));
    };

    const onPointerMove = (event: PointerEvent) => {
      // -1 .. 1 relative to the centre of the viewport.
      nextX = (event.clientX / window.innerWidth) * 2 - 1;
      nextY = (event.clientY / window.innerHeight) * 2 - 1;
      if (!frame) frame = window.requestAnimationFrame(apply);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const occupants = assignSlots(feature);

  return (
    <div className="floating-icons" ref={rootRef} aria-hidden="true">
      {LAYERS.map((layer) => (
        <div
          key={layer}
          className={`floating-icons__layer floating-icons__layer--${layer}`}
          style={{ '--depth': PARALLAX_DEPTH[layer] } as React.CSSProperties}
        >
          {occupants
            .filter((tile) => tile.slot.layer === layer)
            .map((tile, index) => {
              // Each product's bloom is its own brand colour, so the backdrop
              // agrees with the logos printed on it.
              const tint = getBrand(tile.id).accent;
              const size = tile.featured
                ? FEATURED_SIZE[tile.shape]
                : tile.slot[tile.shape];
              return (
                <div
                  key={tile.id}
                  className={`floating-tile floating-tile--${tile.shape}${
                    tile.featured ? ' floating-tile--featured' : ''
                  }`}
                  data-slot={tile.slot.id}
                  data-tile={tile.id}
                  style={
                    {
                      top: tile.slot.top,
                      left: tile.slot.left,
                      '--size': `${size}px`,
                      '--tint': tint,
                      '--tint-rgb': toRgbChannels(tint),
                      '--rotate': `${tile.rotate}deg`,
                      '--enter-delay': `${0.15 + index * 0.14}s`,
                      '--drift-delay': `${index * -4.5}s`,
                    } as React.CSSProperties
                  }
                >
                  <div className="floating-tile__drift">
                    <div className="floating-tile__glass">
                      <img className="floating-tile__logo" src={tile.logo} alt="" />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
};

export default FloatingIcons;
